import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import NoticeModal from './NoticeModal';

const AdminPanel = ({ panelId }) => {
    const [requests, setRequests] = useState([]);
    const _cu = JSON.parse(localStorage.getItem('user') || '{}');
    const _role = _cu.role;
    const canDoDeposit = _role === 'izleyici' ? false : (_role === 'yardimci' ? !!_cu.canDeposit : true);
    const isYonetimRole = _role === 'yonetim' || _role === 'master_admin';
    const isAdminRole = isYonetimRole || _role === 'admin';
    const canMoveDeposit = isAdminRole || _role === 'finansci' || _role === 'destek';
    const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null, danger: false });
    const askConfirm = (message, onConfirm, danger = false) => setConfirmState({ open: true, message, onConfirm, danger });
    const closeConfirm = () => setConfirmState({ open: false, message: '', onConfirm: null, danger: false });
    const [notice, setNotice] = useState({ open: false, message: '', danger: false });
    const showNotice = (message, danger = false) => setNotice({ open: true, message, danger });
    const [filter, setFilter] = useState(''); // 'active', 'approved', 'rejected' vs.
    const isManagement = window.location.hostname.startsWith('yonetim');

    // Detay Paneli State'leri
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [editAmount, setEditAmount] = useState(0);
    const [editNote, setEditNote] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        minAmount: '',
        maxAmount: '',
        status: '',
        searchTerm: ''
    });

    // Yeni İstek Modal State'leri
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newRequest, setNewRequest] = useState({
        username: '',
        name: '',
        accountId: '',
        amount: ''
    });
    // Seçili panelin banka hesapları (manuel yatırımda banka seçimi için)
    const [accounts, setAccounts] = useState([]);

    // Panele taşıma (yönetim admin)
    const [panels, setPanels] = useState([]);
    const [moveTarget, setMoveTarget] = useState('');

    const handleMove = async (id, targetPanelId) => {
        if (!targetPanelId) { showNotice('Lütfen bir departman seçin.', true); return; }
        try {
            await api.moveInvestment(id, targetPanelId);
            setSelectedRequest(null);
            setMoveTarget('');
            fetchRequests();
            logAction('Yatırım Taşıma', `İstek ID: ${id} -> departman ${targetPanelId} taşındı.`);
        } catch (error) {
            showNotice('Taşıma başarısız: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const fetchRequests = useCallback(() => {
        const currentUser = JSON.parse(localStorage.getItem('user'));
        const activePanelId = panelId || currentUser?.panelId;
        api.getInvestments({ status: filter, panelId: activePanelId || '' })
            .then(res => {
                let data = res.data;
                setRequests(data);
            })
            .catch(err => console.error(err));
    }, [filter, panelId]);

    useEffect(() => {
        document.title = 'PayzoPay - Yatırım Havuzu';
        fetchRequests();
        if (isManagement) {
            api.getPanels().then(res => setPanels(res.data)).catch(err => console.error(err));
        }
        const cu = JSON.parse(localStorage.getItem('user') || '{}');
        const activePanelId = panelId || cu.panelId;
        if (activePanelId) {
            api.getAccounts({ panelId: activePanelId }).then(res => setAccounts(res.data)).catch(() => {});
        }
        const interval = setInterval(fetchRequests, 5000);
        return () => clearInterval(interval);
    }, [fetchRequests, isManagement, panelId]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filteredRequests = requests.filter(req => {
        // Arama
        const term = filters.searchTerm.toLowerCase();
        const matchesSearch = !term ||
            (req.redirectbid && req.redirectbid.toLowerCase().includes(term)) ||
            (req.selectedOption?.payerName && req.selectedOption.payerName.toLowerCase().includes(term)) ||
            (req.selectedOption?.accountHolder && req.selectedOption.accountHolder.toLowerCase().includes(term)) ||
            (req.selectedOption?.bankName && req.selectedOption.bankName.toLowerCase().includes(term)) ||
            (req.amount && req.amount.toString().includes(term)) ||
            (req.processedBy && req.processedBy.toLowerCase().includes(term));

        // Tarih
        const reqDate = new Date(req.createdAt);
        const start = filters.startDate ? new Date(filters.startDate) : null;
        const end = filters.endDate ? new Date(filters.endDate) : null;
        if (start) start.setHours(0, 0, 0, 0);
        if (end) end.setHours(23, 59, 59, 999);
        const matchesDate = (!start || reqDate >= start) && (!end || reqDate <= end);

        // Miktar
        const amount = req.amount;
        const min = filters.minAmount ? parseFloat(filters.minAmount) : null;
        const max = filters.maxAmount ? parseFloat(filters.maxAmount) : null;
        const matchesAmount = (!min || amount >= min) && (!max || amount <= max);

        // Durum ("new,processing" gibi virgüllü değerler birden çok durumu kapsar)
        const matchesStatus = !filters.status ||
            (filters.status.includes(',') ? filters.status.split(',').includes(req.status) : req.status === filters.status);

        return matchesSearch && matchesDate && matchesAmount && matchesStatus;
    });


    const logAction = async (action, details) => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || user?.panelId;
            const username = user ? user.username : 'Bilinmiyor';
            await api.createLog({ username, action, details, panelId: activePanelId });
        } catch (error) {
            console.error('Loglama hatası:', error);
        }
    };

    const updateStatus = async (id, newStatus, extraData = {}) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const payload = {
                status: newStatus,
                processedBy: currentUser ? currentUser.username : 'Admin',
                note: editNote,
                ...extraData
            };
            await api.updateInvestmentStatus(id, payload);
            fetchRequests(); // Listeyi yenile
            logAction('Yatırım Durum Güncelleme', `İstek ID: ${id}, Yeni Durum: ${newStatus}`);
        } catch (error) {
            showNotice('Güncelleme başarısız.', true);
        }
    };

    const handleSaveAmount = async () => {
        if (!selectedRequest) return;
        try {
            await api.updateInvestmentAmount(selectedRequest._id, { amount: editAmount, note: editNote });
            setSelectedRequest(prev => prev ? { ...prev, amount: editAmount, note: editNote } : prev);
            fetchRequests();
            logAction('Yatırım Miktar Güncelleme', `İstek ID: ${selectedRequest._id}, Yeni Miktar: ${editAmount}`);
            showNotice('Miktar kaydedildi.');
        } catch (error) {
            showNotice('Miktar kaydedilemedi.', true);
        }
    };

    const handleRowClick = (req) => {
        setSelectedRequest(req);
        setEditAmount(req.amount || 0);
        setEditNote(req.note || '');
    };

    const handleProcess = async () => {
        if (!selectedRequest) return;
        askConfirm('Bu yatırım talebini işleme almak istediğinize emin misiniz?', async () => {
            await updateStatus(selectedRequest._id, 'processing', { amount: editAmount });
            setSelectedRequest(prev => ({ ...prev, status: 'processing' }));
        });
    };

    const handleDecision = async (decision) => {
        if (!selectedRequest) return;
        const txt = decision === 'approved' ? 'ONAYLAMAK' : 'REDDETMEK';
        askConfirm(`Bu yatırım talebini ${txt} istediğinize emin misiniz?`, async () => {
            await updateStatus(selectedRequest._id, decision, { amount: editAmount });
            setSelectedRequest(null); // İşlem bitince paneli kapat
        }, decision === 'rejected');
    };

    const handleDelete = async (id) => {
        try {
            await api.deleteInvestment(id);
            fetchRequests();
            setSelectedRequest(null);
            logAction('Yatırım Silme', `İstek ID: ${id} silindi.`);
        } catch (error) {
            showNotice('Silme başarısız.', true);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'new': return '#5D9CEC'; // Pastel Mavi
            case 'processing': return '#F6BB42'; // Pastel Turuncu
            case 'approved': return '#A0D468'; // Pastel Yeşil
            case 'rejected': return '#ED5565'; // Pastel Kırmızı
            default: return '#333';
        }
    };

    const getStatusRowBackground = (status) => {
        switch (status) {
            case 'new': return 'rgba(93, 156, 236, 0.5)';
            case 'processing': return 'rgba(246, 187, 66, 0.5)';
            case 'approved': return 'rgba(160, 212, 104, 0.5)';
            case 'rejected': return 'rgba(237, 85, 101, 0.5)';
            default: return 'transparent';
        }
    };

    const labelStyle = { fontWeight: 'bold', fontSize: '0.9em', marginBottom: '5px', display: 'block', color: 'var(--text-color)' };
    const inputStyle = { width: '100%', padding: '8px', border: '1px solid var(--input-border)', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' };

    const cardStyle = {
        backgroundColor: 'var(--card-bg)',
        borderRadius: '15px',
        boxShadow: 'var(--shadow)',
        overflow: 'hidden',
        marginBottom: '20px',
        color: 'var(--text-color)'
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setNewRequest(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        const isPanelDeleted = isManagement && !!panelId && panels.length > 0 && !panels.some(p => p.panelId === panelId);
        if (isPanelDeleted) {
            showNotice('Bu departman silinmiş veya bulunamadı. Yeni yatırım eklenemez.', true);
            return;
        }
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            const acc = accounts.find(a => a._id === newRequest.accountId);
            if (!acc) { showNotice('Lütfen bir banka hesabı seçin.', true); return; }
            await api.createInvestment({
                redirectbid: newRequest.username,
                payerName: newRequest.name,
                amount: newRequest.amount,
                bankName: acc.bankName,
                accountHolder: acc.accountHolder,
                iban: acc.iban,
                panelId: activePanelId
            });
            setShowCreateModal(false);
            setNewRequest({ username: '', name: '', accountId: '', amount: '' });
            fetchRequests();
        } catch (error) {
            showNotice('Oluşturulurken hata oluştu: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const isPanelDeleted = isManagement && !!panelId && panels.length > 0 && !panels.some(p => p.panelId === panelId);

    return (
        <div style={{ padding: '20px' }}>
            {isPanelDeleted && (
                <div style={{ padding: '12px 16px', background: 'rgba(220,53,69,0.15)', border: '1px solid #dc3545', borderRadius: '8px', color: '#dc3545', marginBottom: '15px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-exclamation-triangle"></i>
                    Bu departman silinmiş veya bulunamadı. Yeni yatırım eklenemez.
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: '#28a745', color: '#fff', padding: '4px 14px', borderRadius: '6px', fontSize: '0.6em', fontWeight: 'bold', letterSpacing: '1px' }}>YATIRIM</span>
                    Yatırım Havuzu
                </h2>
                <div>
                    {isManagement && canDoDeposit && !isPanelDeleted && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: 'none',
                                backgroundColor: '#28a745',
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                marginRight: '10px'
                            }}
                        >
                            + Yeni Ekle
                        </button>
                    )}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: 'none',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                        }}
                    >
                        {showFilters ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
                    </button>
                </div>
            </div>

            {/* Filtre Alanı (Expandable) */}
            {showFilters && (
                <div style={{ ...cardStyle, padding: '20px', backgroundColor: 'var(--table-header-bg)' }}>
                    <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                            {/* Tarih Filtreleri */}
                            <div>
                                <label style={labelStyle}>Başlangıç Tarihi</label>
                                <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Bitiş Tarihi</label>
                                <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={inputStyle} />
                            </div>

                            {/* Miktar Filtreleri */}
                            <div>
                                <label style={labelStyle}>Minimum Yatırım</label>
                                <input type="number" name="minAmount" placeholder="0" value={filters.minAmount} onChange={handleFilterChange} style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Maksimum Yatırım</label>
                                <input type="number" name="maxAmount" placeholder="0" value={filters.maxAmount} onChange={handleFilterChange} style={inputStyle} />
                            </div>

                            {/* Dropdownlar */}
                            <div>
                                <label style={labelStyle}>İşlem Durumu</label>
                                <select name="status" value={filters.status} onChange={handleFilterChange} style={inputStyle}>
                                    <option value="">Hepsi</option>
                                    <option value="new">Yeni</option>
                                    <option value="processing">İşlemde</option>
                                    <option value="new,processing">Yeni + İşlemde</option>
                                    <option value="approved">Onaylandı</option>
                                    <option value="rejected">Reddedildi</option>
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Site</label>
                                <select style={inputStyle}>
                                    <option value="">Hepsi</option>
                                    <option value="Yatırım">Yatırım</option>
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Finans Grubu</label>
                                <select style={inputStyle}>
                                    <option value="">Hepsi</option>
                                    <option value="Banka">Banka</option>
                                </select>
                            </div>
                        </div>

                        {/* Arama */}
                        <div style={{ marginTop: '15px' }}>
                            <label style={labelStyle}>Arama</label>
                            <input type="text" name="searchTerm" placeholder="İsim, Kullanıcı, Herşey..." value={filters.searchTerm} onChange={handleFilterChange} style={inputStyle} />
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                {/* Sol Taraf: Tablo */}
                <div style={{ flex: selectedRequest ? '0 0 70%' : '1', transition: 'all 0.3s' }}>
                    <div style={cardStyle}>
                        <h3 style={{ margin: '20px 0 10px 20px', color: 'var(--text-color)' }}>Tüm Talepler</h3>
                        <div style={{ padding: '0 20px 20px 20px' }}>
                            <table cellPadding="15" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px', color: 'var(--text-color)' }}>
                                <thead>
                                    <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                                        <th>ID</th>
                                        <th>İsim Soyisim</th>
                                        <th>Kullanıcı Adı</th>
                                        <th>Banka Adı</th>
                                        <th>Miktar</th>
                                        <th>İşlemi Yapan</th>
                                        <th>Tarih</th>
                                        <th style={{ width: '120px', textAlign: 'center' }}>Durum</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRequests.map(req => {
                                        const isSelected = selectedRequest?._id === req._id;
                                        const borderStyle = isSelected ? '2px solid rgba(0,0,0,0.6)' : 'none';
                                        const radius = '15px';
                                        const rowBg = getStatusRowBackground(req.status);

                                        return (
                                            <tr
                                                key={req._id}
                                                onClick={() => handleRowClick(req)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td style={{ backgroundColor: rowBg, borderTop: borderStyle, borderBottom: borderStyle, borderLeft: borderStyle, borderTopLeftRadius: radius, borderBottomLeftRadius: radius }}>{req.siteTransactionNo || ('#' + String(req._id).slice(-6))}</td>
                                                <td style={{ backgroundColor: rowBg, borderTop: borderStyle, borderBottom: borderStyle }}>{req.selectedOption?.payerName || req.selectedOption?.accountHolder || '-'}</td>
                                                <td style={{ backgroundColor: rowBg, borderTop: borderStyle, borderBottom: borderStyle }}>{req.redirectbid}</td>
                                                <td style={{ backgroundColor: rowBg, borderTop: borderStyle, borderBottom: borderStyle }}>{req.selectedOption?.bankName || '-'}</td>
                                                <td style={{ backgroundColor: rowBg, borderTop: borderStyle, borderBottom: borderStyle }}>{Number(req.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                <td style={{ backgroundColor: rowBg, borderTop: borderStyle, borderBottom: borderStyle }}>{req.processedBy || '-'}</td>
                                                <td style={{ backgroundColor: rowBg, borderTop: borderStyle, borderBottom: borderStyle }}>{new Date(req.createdAt).toLocaleString()}</td>
                                                <td style={{ backgroundColor: rowBg, padding: 0, height: '1px', borderTop: borderStyle, borderBottom: borderStyle, borderRight: borderStyle, borderTopRightRadius: radius, borderBottomRightRadius: radius, overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: getStatusColor(req.status),
                                                        color: '#fff',
                                                        fontWeight: 'bold',
                                                        minHeight: '40px'
                                                    }}>
                                                        {req.status === 'new' ? 'YENİ' : req.status === 'processing' ? 'İŞLEMDE' : req.status === 'approved' ? 'ONAYLANDI' : req.status === 'rejected' ? 'REDDEDİLDİ' : req.status.toUpperCase()}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Sağ Taraf: Detay Paneli */}
                {selectedRequest && (
                    <div style={{ flex: '1', ...cardStyle, backgroundColor: 'var(--card-bg)', padding: 0, position: 'sticky', top: '20px' }}>
                        <div style={{ backgroundColor: getStatusRowBackground(selectedRequest.status), padding: '20px', height: '100%' }}>
                            <h3 style={{ marginTop: 0, borderBottom: '2px solid var(--border-color)', paddingBottom: '15px', color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ background: '#28a745', color: '#fff', padding: '4px 12px', borderRadius: '6px', fontSize: '0.7em', fontWeight: 'bold', letterSpacing: '1px' }}>YATIRIM</span>
                                İşlem Detayları
                            </h3>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px', fontSize: '0.9em' }}>
                                <div><strong>İsim:</strong> <br /> {selectedRequest.selectedOption?.payerName || selectedRequest.selectedOption?.accountHolder || '-'}</div>
                                <div><strong>Kullanıcı Adı:</strong> <br /> {selectedRequest.redirectbid}</div>

                                <div><strong>Site İşlem ID:</strong> <br /> <span style={{ fontSize: '0.8em' }}>{selectedRequest._id}</span></div>
                                <div><strong>İşlem Durumu:</strong> <br /> {selectedRequest.status}</div>

                                <div><strong>Banka:</strong> <br /> {selectedRequest.selectedOption?.bankName || '-'}</div>
                                <div><strong>Hesap:</strong> <br /> {selectedRequest.selectedOption?.accountHolder || '-'}</div>

                                <div style={{ gridColumn: 'span 2' }}><strong>Hesap No (IBAN):</strong> <br /> {selectedRequest.selectedOption?.iban || '-'}</div>

                                <div><strong>Operatör:</strong> <br /> {selectedRequest.processedBy || '-'}</div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Not</label>
                                <textarea
                                    value={editNote}
                                    onChange={(e) => setEditNote(e.target.value)}
                                    disabled={selectedRequest.status === 'approved' || selectedRequest.status === 'rejected'}
                                    style={{
                                        width: '100%', padding: '8px', border: '1px solid var(--input-border)',
                                        borderRadius: '4px', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)',
                                        minHeight: '60px', resize: 'vertical'
                                    }}
                                    placeholder="İşlem notu..."
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Miktar (Düzenlenebilir)</label>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <input
                                        type="number"
                                        value={editAmount}
                                        onChange={(e) => setEditAmount(e.target.value)}
                                        style={{ flex: 1, padding: '8px', border: '1px solid var(--input-border)', borderRadius: '4px', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                        disabled={selectedRequest.status === 'approved' || selectedRequest.status === 'rejected'}
                                    />
                                    {canDoDeposit && selectedRequest.status !== 'approved' && selectedRequest.status !== 'rejected' && (
                                        <button
                                            type="button"
                                            onClick={handleSaveAmount}
                                            style={{ padding: '8px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                                        >
                                            Kaydet
                                        </button>
                                    )}
                                </div>
                                <div style={{ fontSize: '0.8em', opacity: 0.7, marginTop: '4px' }}>Onaylamadan/reddetmeden sadece miktarı kaydeder.</div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {canDoDeposit && selectedRequest.status === 'new' && (
                                    <button onClick={handleProcess} style={{ padding: '10px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>İşleme Al</button>
                                )}
                                {canDoDeposit && selectedRequest.status === 'processing' && (
                                    <>
                                        <button onClick={() => handleDecision('approved')} style={{ padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Onayla</button>
                                        <button onClick={() => handleDecision('rejected')} style={{ padding: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Reddet</button>
                                    </>
                                )}
                                {isManagement && isYonetimRole && (
                                    <button onClick={() => askConfirm('Bu yatırım talebini SİLMEK istediğinize emin misiniz? Bu işlem geri alınamaz.', () => handleDelete(selectedRequest._id), true)} style={{ padding: '10px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Sil</button>
                                )}
                            </div>

                            {isManagement && canMoveDeposit && (
                                <div style={{ marginTop: '15px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
                                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px' }}>Başka Departmana Taşı:</label>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-color)' }}>
                                            <option value="">Departman seçin...</option>
                                            {panels.filter(p => p.panelId !== selectedRequest.panelId).map(p => (
                                                <option key={p._id} value={p.panelId}>{p.name || p.panelId}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => {
                                                const target = moveTarget;
                                                const tName = (panels.find(p => p.panelId === target) || {}).name || target;
                                                if (!target) { showNotice('Lütfen bir departman seçin.', true); return; }
                                                askConfirm(`Bu yatırımı "${tName}" departmanına taşımak istediğinize emin misiniz?`, () => handleMove(selectedRequest._id, target));
                                            }}
                                            style={{ padding: '8px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                                        >
                                            Taşı
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '400px', color: 'var(--text-color)' }}>
                        <h3>Yeni Yatırım İsteği</h3>
                        <form onSubmit={handleCreateSubmit}>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Kullanıcı Adı</label>
                                <input name="username" value={newRequest.username} onChange={handleCreateChange} required style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>İsim Soyisim</label>
                                <input name="name" value={newRequest.name} onChange={handleCreateChange} required style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Banka</label>
                                <select name="accountId" value={newRequest.accountId} onChange={handleCreateChange} required style={inputStyle}>
                                    <option value="">Banka hesabı seçiniz...</option>
                                    {accounts.map(a => (
                                        <option key={a._id} value={a._id}>{a.bankName} — {a.accountHolder} — {a.iban}</option>
                                    ))}
                                </select>
                                {accounts.length === 0 && (
                                    <div style={{ fontSize: '0.8em', color: '#dc3545', marginTop: '4px' }}>Bu departmana tanımlı banka hesabı yok. Hesaplar sayfasından ekleyin.</div>
                                )}
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Miktar</label>
                                <input type="number" name="amount" value={newRequest.amount} onChange={handleCreateChange} required style={inputStyle} />
                            </div>
                            <div style={{ textAlign: 'right', marginTop: '20px' }}>
                                <button type="button" onClick={() => setShowCreateModal(false)} style={{ marginRight: '10px', padding: '8px 16px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>İptal</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                open={confirmState.open}
                message={confirmState.message}
                danger={confirmState.danger}
                onConfirm={() => { if (confirmState.onConfirm) confirmState.onConfirm(); closeConfirm(); }}
                onCancel={closeConfirm}
            />

            <NoticeModal
                open={notice.open}
                message={notice.message}
                danger={notice.danger}
                onClose={() => setNotice({ open: false, message: '', danger: false })}
            />
        </div>
    );
};

export default AdminPanel;